import { useState, useEffect, useMemo } from "react";
import { listRecommendations, createRecommendation, updateRecommendation } from "../lib/adminApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Pencil, Check, X, Search, ChevronDown, ChevronRight } from "lucide-react";

// Business-friendly display labels for each underlying type key.
const TYPE_LABELS = {
  recommendation_text: "Recommendation Text",
  deployment_text: "Deployment Text",
  reason_text: "Reason Text",
  feature_flag_label: "Feature Flag Label",
};
const TYPE_ORDER = ["recommendation_text", "deployment_text", "reason_text", "feature_flag_label"];
// Section headers (plural where it reads better).
const SECTION_LABELS = {
  recommendation_text: "Recommendation Text",
  deployment_text: "Deployment Text",
  reason_text: "Reason Text",
  feature_flag_label: "Feature Flag Labels",
};

// Application catalogue. Only SmartEvaluate is populated today; the others are
// reserved so the content library can hold their copy in future.
const APPLICATIONS = [
  { value: "smartevaluate", label: "SmartEvaluate" },
  { value: "smartbid", label: "SmartBid" },
  { value: "smartimprove", label: "SmartImprove" },
  { value: "smartewm", label: "SmartEWM" },
  { value: "smartinspect", label: "SmartInspect" },
];
const APP_LABELS = Object.fromEntries(APPLICATIONS.map((a) => [a.value, a.label]));

const SCENARIOS = [
  { value: "greenfield", label: "Greenfield", dot: "#16A34A", bg: "#DCFCE7", text: "#166534" },
  { value: "brownfield", label: "Brownfield", dot: "#EA580C", bg: "#FFEDD5", text: "#9A3412" },
  { value: "cloud_migration", label: "Cloud Migration", dot: "#2563EB", bg: "#DBEAFE", text: "#1E40AF" },
];
const SCENARIO_MAP = Object.fromEntries(SCENARIOS.map((s) => [s.value, s]));

const empty = {
  recommendation_key: "",
  recommendation_type: "recommendation_text",
  label: "",
  description: "",
  active: true,
  application: "smartevaluate",
  scenario_types: ["greenfield", "brownfield"],
};

function ScenarioBadge({ value }) {
  const s = SCENARIO_MAP[value];
  if (!s) return null;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-semibold whitespace-nowrap" style={{ backgroundColor: s.bg, color: s.text }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.dot }} />
      {s.label}
    </span>
  );
}

export default function RecommendationsPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [msg, setMsg] = useState("");
  // Filters
  const [fApp, setFApp] = useState("all");
  const [fScenario, setFScenario] = useState("all");
  const [fType, setFType] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [search, setSearch] = useState("");
  // Collapsible section state — all expanded by default.
  const [openSections, setOpenSections] = useState({
    recommendation_text: true, deployment_text: true, reason_text: true, feature_flag_label: true,
  });
  const toggleSection = (t) => setOpenSections((prev) => ({ ...prev, [t]: !prev[t] }));

  const load = () => listRecommendations().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(empty);
    setShowForm(true);
  };
  const openEdit = (rec) => {
    setEditing(rec.id);
    setForm({
      recommendation_key: rec.recommendation_key || "",
      recommendation_type: rec.recommendation_type || "recommendation_text",
      label: rec.label || "",
      description: rec.description || "",
      active: rec.active !== false,
      application: rec.application || "smartevaluate",
      scenario_types: Array.isArray(rec.scenario_types) && rec.scenario_types.length ? rec.scenario_types : ["greenfield", "brownfield"],
    });
    setShowForm(true);
  };
  const closeForm = () => {
    setEditing(null);
    setForm(empty);
    setShowForm(false);
  };

  const toggleScenario = (v) => {
    const cur = new Set(form.scenario_types || []);
    if (cur.has(v)) cur.delete(v); else cur.add(v);
    setForm({ ...form, scenario_types: SCENARIOS.map((s) => s.value).filter((x) => cur.has(x)) });
  };

  const save = async () => {
    try {
      if (editing) await updateRecommendation(editing, form);
      else await createRecommendation(form);
      setMsg("Saved");
      closeForm();
      await load();
      setTimeout(() => setMsg(""), 2000);
    } catch {
      setMsg("Error saving");
    }
  };

  const filtered = useMemo(() => {
    let list = [...items];
    if (fApp !== "all") list = list.filter((r) => (r.application || "smartevaluate") === fApp);
    if (fScenario !== "all") list = list.filter((r) => (r.scenario_types || ["greenfield", "brownfield"]).includes(fScenario));
    if (fType !== "all") list = list.filter((r) => r.recommendation_type === fType);
    if (fStatus !== "all") list = list.filter((r) => (fStatus === "active" ? r.active !== false : r.active === false));
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      list = list.filter((r) => (r.label || "").toLowerCase().includes(s) || (r.recommendation_key || "").toLowerCase().includes(s));
    }
    return list.sort((a, b) => TYPE_ORDER.indexOf(a.recommendation_type) - TYPE_ORDER.indexOf(b.recommendation_type) || (a.label || "").localeCompare(b.label || ""));
  }, [items, fApp, fScenario, fType, fStatus, search]);

  // Group filtered items by recommendation_type; empty groups auto-hide.
  const groups = useMemo(() => {
    const g = {};
    filtered.forEach((r) => { (g[r.recommendation_type] = g[r.recommendation_type] || []).push(r); });
    const ordered = TYPE_ORDER.filter((t) => g[t]?.length);
    const others = Object.keys(g).filter((t) => !TYPE_ORDER.includes(t));
    return [...ordered, ...others].map((t) => ({ type: t, label: SECTION_LABELS[t] || TYPE_LABELS[t] || t, recs: g[t] }));
  }, [filtered]);

  const filtersActive = fApp !== "all" || fScenario !== "all" || fType !== "all" || fStatus !== "all" || search.trim() !== "";

  return (
    <div data-testid="admin-recommendations-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>Recommendation Library</h1>
          <p className="text-[11px] text-[#6B7280] mt-0.5">
            Business-friendly copy that powers recommendation narratives across consultant and client views. This is a content repository — the decision logic lives in the scoring engine and capability triggers.
          </p>
        </div>
        {!showForm && (
          <Button data-testid="rec-add-btn" onClick={openAdd} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs">
            <Plus className="w-3.5 h-3.5 mr-1" />New Recommendation
          </Button>
        )}
      </div>

      {msg && (
        <div data-testid="rec-save-msg" className="mb-3 px-3 py-2 bg-[#E0F2FE] border border-[#025979]/20 rounded-sm text-xs text-[#025979]">
          {msg}
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="rec-filters">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input data-testid="rec-filter-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search label or key…" className="rounded-sm text-xs h-8 pl-8" />
        </div>
        <Select value={fApp} onValueChange={setFApp}>
          <SelectTrigger data-testid="rec-filter-application" className="rounded-sm text-xs h-8 w-40"><SelectValue placeholder="Application" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Applications</SelectItem>
            {APPLICATIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fScenario} onValueChange={setFScenario}>
          <SelectTrigger data-testid="rec-filter-scenario" className="rounded-sm text-xs h-8 w-36"><SelectValue placeholder="Scenario" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Scenarios</SelectItem>
            {SCENARIOS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fType} onValueChange={setFType}>
          <SelectTrigger data-testid="rec-filter-type" className="rounded-sm text-xs h-8 w-40"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {TYPE_ORDER.map((t) => <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger data-testid="rec-filter-status" className="rounded-sm text-xs h-8 w-32"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        {filtersActive && (
          <button data-testid="rec-clear-filters" onClick={() => { setSearch(""); setFApp("all"); setFScenario("all"); setFType("all"); setFStatus("all"); }} className="text-xs text-[#025979] hover:text-[#03738C] font-medium">Clear</button>
        )}
      </div>

      {/* Add/Edit form */}
      {showForm && (
        <div data-testid="rec-form" className="bg-white border border-[#025979]/30 rounded-sm shadow-sm p-5 mb-5">
          <h2 className="text-sm font-semibold text-[#111827] mb-4">{editing ? "Edit" : "Add"} Recommendation</h2>

          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF] mb-2">General</p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-[11px] font-medium text-[#374151] mb-1 block">Recommendation Key {editing && <span className="text-[#9CA3AF] font-normal">(read only)</span>}</label>
              <Input data-testid="rec-form-key" placeholder="e.g. ewm_advanced_rec" value={form.recommendation_key} readOnly={!!editing}
                onChange={(e) => setForm({ ...form, recommendation_key: e.target.value })}
                className={`rounded-sm text-xs ${editing ? "bg-gray-50 text-[#6B7280] cursor-not-allowed" : ""}`} />
            </div>
            <div>
              <label className="text-[11px] font-medium text-[#374151] mb-1 block">Label</label>
              <Input data-testid="rec-form-label" placeholder="Label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="rounded-sm text-xs" />
            </div>
            <div className="col-span-2">
              <label className="text-[11px] font-medium text-[#374151] mb-1 block">Description</label>
              <Textarea data-testid="rec-form-description" placeholder="Description (used as narrative copy)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm text-xs" rows={3} />
            </div>
          </div>

          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF] mb-2">Classification</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-medium text-[#374151] mb-1 block">Application</label>
              <Select value={form.application} onValueChange={(v) => setForm({ ...form, application: v })}>
                <SelectTrigger data-testid="rec-form-application" className="rounded-sm text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{APPLICATIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] font-medium text-[#374151] mb-1 block">Recommendation Type</label>
              <Select value={form.recommendation_type} onValueChange={(v) => setForm({ ...form, recommendation_type: v })}>
                <SelectTrigger data-testid="rec-form-type" className="rounded-sm text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPE_ORDER.map((t) => <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <label className="text-[11px] font-medium text-[#374151] mb-1.5 block">Scenario Types</label>
              <div className="flex flex-wrap gap-4" data-testid="rec-form-scenarios">
                {SCENARIOS.map((s) => (
                  <label key={s.value} className="text-xs font-medium text-[#374151] flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" data-testid={`rec-form-scenario-${s.value}`} checked={(form.scenario_types || []).includes(s.value)} onChange={() => toggleScenario(s.value)} className="rounded-sm" />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <label className="text-xs text-[#374151]">Active</label>
              <input data-testid="rec-form-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            </div>
          </div>

          <div className="mt-4 flex gap-2 border-t border-[#E5E7EB] pt-4">
            <Button data-testid="rec-form-save" onClick={save} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"><Check className="w-3 h-3 mr-1" />Save</Button>
            <Button data-testid="rec-form-cancel" variant="outline" onClick={closeForm} className="rounded-sm text-xs"><X className="w-3 h-3 mr-1" />Cancel</Button>
          </div>
        </div>
      )}

      {/* Grouped collapsible sections */}
      <div className="space-y-3" data-testid="rec-groups">
        {groups.map(({ type, label, recs }) => {
          const open = openSections[type] !== false;
          return (
            <div key={type} className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden" data-testid={`rec-group-${type}`}>
              <button
                type="button"
                data-testid={`rec-group-toggle-${type}`}
                onClick={() => toggleSection(type)}
                className="w-full px-4 py-3 bg-gray-50 border-b border-[#E5E7EB] flex items-center gap-2 text-left hover:bg-gray-100 transition-colors"
              >
                {open ? <ChevronDown className="w-4 h-4 text-[#6B7280]" /> : <ChevronRight className="w-4 h-4 text-[#6B7280]" />}
                <span className="text-xs font-semibold text-[#111827]">{label}</span>
                <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-[#E0F2FE] text-[#025979] font-medium">{recs.length}</span>
              </button>
              {open && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-[#E5E7EB]">
                        <th className="text-left px-4 py-2 text-[#6B7280] font-medium min-w-[320px]">Recommendation</th>
                        <th className="text-left px-3 py-2 text-[#6B7280] font-medium w-28">Application</th>
                        <th className="text-left px-3 py-2 text-[#6B7280] font-medium w-40">Scenario(s)</th>
                        <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-16">Status</th>
                        <th className="text-right px-3 py-2 text-[#6B7280] font-medium w-14">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recs.map((r) => (
                        <tr key={r.id} data-testid={`rec-row-${r.recommendation_key}`} className="border-b border-[#E5E7EB] last:border-0 hover:bg-gray-50 transition-colors align-top">
                          <td className="px-4 py-3 text-[#111827]">
                            <div className="font-semibold text-sm leading-snug">{r.label}</div>
                            <div className="text-[13px] text-[#4B5563] mt-1 leading-relaxed line-clamp-2" title={r.description}>{r.description}</div>
                          </td>
                          <td className="px-3 py-3 text-[11px] text-[#6B7280]">{APP_LABELS[r.application] || r.application || "SmartEvaluate"}</td>
                          <td className="px-3 py-3">
                            <div className="flex flex-wrap gap-1">
                              {(r.scenario_types || ["greenfield", "brownfield"]).map((sc) => <ScenarioBadge key={sc} value={sc} />)}
                            </div>
                          </td>
                          <td className="px-2 py-3 text-center">
                            <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-semibold ${r.active !== false ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#F3F4F6] text-[#9CA3AF]"}`}>
                              {r.active !== false ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <button data-testid={`rec-edit-${r.recommendation_key}`} onClick={() => openEdit(r)} className="text-[#025979] hover:text-[#03738C]" aria-label="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
        {groups.length === 0 && (
          <div className="bg-white border border-[#E5E7EB] rounded-sm p-8 text-center text-xs text-[#9CA3AF]" data-testid="rec-empty">
            {filtersActive ? "No entries match the current filters." : (
              <>No entries yet. <button onClick={openAdd} className="text-[#025979] hover:text-[#03738C] font-medium">Add one</button></>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
